/**
 * SO-3a — a punch item on the party's own link, on its own page.
 *
 *   /site-office/:token/punch             the whole list, grouped
 *   /site-office/:token/punch/:itemId     one item: the photo, what it says,
 *                                         the two actions, and the thread
 *
 * The office payload (GET /api/site-office-links/{token}) carries the firm, the
 * party and the rows; the item's own door carries the detail and the thread.
 * Tokens only — no colour, size or family here or in site-office.css.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { cacheBranding } from '@/utils/skin';
import OfficeStrip, { parsePreview, withPreview } from '@/components/site-office/OfficeStrip';
import '@/styles/site-office.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

export const abs = (u) => (u && u.startsWith('/api') ? `${API}${u.replace(/^\/api/, '')}` : u);

// SO-3a vocabulary: the party never reads the word "Open".
export const punchWord = (state) => (state === 'closed' ? 'Closed'
  : state === 'ready_for_review' ? 'Ready for review' : 'To do');

export const punchTagClass = (state) => (state === 'closed' ? 'kso-tag ok'
  : state === 'ready_for_review' ? 'kso-tag wait' : 'kso-tag');

export const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const punchLine = (item) => [
  item.room || item.location,
  item.visit_label ? `from ${item.visit_label}` : '',
  item.assigned_at ? `assigned ${shortDate(item.assigned_at)}` : '',
].filter(Boolean).join(' · ');

/** One row, on the home card and on the list. The row IS the way in. */
export const PunchRow = ({ token, item, preview }) => {
  const shot = (item.photos || [])[0];
  const thumb = shot && !shot.unavailable ? abs(shot.thumb_url || shot.full_url) : null;
  return (
    <Link className="kso-row link" to={withPreview(`/site-office/${token}/punch/${item.id}`, preview)}
      data-testid={`site-office-punch-row-${item.id}`}>
      {thumb ? <img className="th" src={thumb} alt="" loading="lazy" />
        : <span className="th" />}
      <div className="t">
        <b>{item.title}</b>
        <small>{punchLine(item)}</small>
      </div>
      <span className={punchTagClass(item.state)}
        data-testid={`site-office-punch-row-${item.id}-status`}>
        {punchWord(item.state)}
      </span>
      <span className="chev">›</span>
    </Link>
  );
};

const Hero = ({ photo, alt }) => {
  if (!photo || photo.unavailable || !photo.full_url) {
    return (
      <span className="kso-ph kso-ph--gone kso-item__hero" data-testid="site-office-item-photo-gone">
        Picture no longer available
      </span>
    );
  }
  if (photo.media_type === 'video') {
    return (
      <video className="kso-item__hero" controls preload="metadata" playsInline
        poster={abs(photo.thumb_url) || undefined} src={abs(photo.full_url)}
        data-testid="site-office-item-video" />
    );
  }
  return (
    <a href={abs(photo.full_url)} target="_blank" rel="noreferrer"
      data-testid="site-office-item-photo-open">
      <img className="kso-item__hero" src={abs(photo.thumb_url || photo.full_url)} alt={alt} />
    </a>
  );
};

export default function SiteOfficePunchItem() {
  const { token, itemId } = useParams();
  const navigate = useNavigate();
  // SO-9a2 — the firm reading the list or an item from the preview.
  const [params] = useSearchParams();
  const preview = parsePreview(params.get('preview'));
  const [office, setOffice] = useState(null);
  const [item, setItem] = useState(null);
  const [comments, setComments] = useState([]);
  const [gone, setGone] = useState('');
  const [note, setNote] = useState('');
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');
  const [draft, setDraft] = useState('');

  useEffect(() => {
    bare.get(`${API}/site-office-links/${token}`)
      .then((r) => { setOffice(r.data); if (r.data?.branding) cacheBranding(r.data.branding); })
      .catch(() => setGone('This link is not valid'));
  }, [token]);

  const loadItem = useCallback(async () => {
    if (!itemId) return;
    try {
      const [detail, thread] = await Promise.all([
        bare.get(`${API}/site-office-links/${token}/punch/${itemId}`),
        bare.get(`${API}/site-office-links/${token}/punch/${itemId}/comments`),
      ]);
      setItem(detail.data.item);
      // ST-3b — the server says when a tagged item is waiting on its placer.
      setPending(detail.data.line || '');
      setComments(thread.data.comments || []);
    } catch (_e) {
      setGone('That item is not on your list');
    }
  }, [token, itemId]);

  useEffect(() => { loadItem(); }, [loadItem]);

  const firm = office?.firm_name || '';
  const firmShort = String(firm).split(',')[0];

  // ST-3b — the line the server gives back when the tag waits for an OK.
  const [pending, setPending] = useState('');

  const markDone = async () => {
    if (!shots.length) { setError('A photo of the finished work is required'); return; }
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('note', note);
      shots.forEach((f) => form.append('files', f));
      const answer = await bare.post(
        `${API}/site-office-links/${token}/punch/${itemId}/complete`, form);
      setShots([]); setNote('');
      // ST-3b — an item that came off a site tag is not ours to close: it
      // waits on whoever placed the tag, and the server says whose OK.
      if (answer?.data?.line) setPending(answer.data.line);
      await loadItem();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' && detail.length < 200 ? detail : 'That did not save. Try again.');
    } finally { setBusy(false); }
  };

  const say = async (text, kind) => {
    const body = { text: String(text || '').trim(), kind };
    if (!body.text) return;
    try {
      const r = await bare.post(`${API}/site-office-links/${token}/punch/${itemId}/comments`, body);
      setComments((prev) => [...prev, r.data]);
      if (kind === 'question') { setQuestion(''); setAsking(false); setItem((p) => ({ ...p, needs_answer: true })); }
      else setDraft('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'That did not send. Try again.');
    }
  };

  if (gone) {
    return (
      <main className="kso-page" data-testid="site-office-punch">
        <div className="kso-wrap"><div className="kso-gate"><h1>{gone}</h1></div></div>
      </main>
    );
  }

  if (!office || (itemId && !item)) {
    return (
      <main className="kso-page" data-testid="site-office-punch">
        <div className="kso-wrap"><div className="kso-gate"><p>Opening…</p></div></div>
      </main>
    );
  }

  const head = (
    <header className="kso-head">
      {office.branding?.logo_url ? <img className="kso-head__logo" src={office.branding.logo_url} alt="" /> : null}
      <div className="kso-head__k"><b>{firm}</b> · Site Office</div>
      <div className="kso-head__who">
        <div className="n">{office.party?.name}</div>
        <div className="r">{office.project?.name}</div>
      </div>
    </header>
  );

  const lockedTitle = preview ? `Only ${office?.party?.name} can do this` : undefined;
  const strip = (page) => (preview ? (
    <OfficeStrip projectId={preview.projectId} membershipId={preview.membershipId}
      projectName={office.project?.name} partyName={office.party?.name} page={page} />
  ) : null);

  const crumb = (title) => (
    <div className="kso-crumb" data-testid="site-office-punch-crumb">
      <button type="button" className="back"
        onClick={() => navigate(title
          ? withPreview(`/site-office/${token}/punch`, preview)
          : (preview ? `/projects/${preview.projectId}/site-office/${preview.membershipId}?view=party` : `/site-office/${token}`))}
        data-testid="site-office-punch-back">‹ Punch list</button>
      <span>Site Office</span>
      <span>›</span>
      {title ? <><span>Punch list</span><span>›</span><b>{title}</b></> : <b>Punch list</b>}
    </div>
  );

  // The whole list, in the order the work runs: to do, waiting, done.
  if (!itemId) {
    const rows = office.punch || [];
    const groups = [
      ['To do', rows.filter((r) => r.state === 'open')],
      ['Ready for review', rows.filter((r) => r.state === 'ready_for_review')],
      ['Closed', rows.filter((r) => r.state === 'closed')],
    ].filter(([, list]) => list.length);
    return (
      <main className="kso-page" data-testid="site-office-punch-list">
        <div className="kso-wrap">
          {strip('Punch list')}
          {head}
          {crumb('')}
          <div className="kso-grid">
          {groups.map(([label, list]) => (
            <section className="kso-card w12" key={label} data-testid={`site-office-punch-group-${label.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="kso-card__hd">{label}<span className="n">{list.length}</span></div>
              <div className="kso-card__bd">
                {list.map((row) => <PunchRow key={row.id} token={token} item={row} preview={preview} />)}
              </div>
            </section>
          ))}
          </div>
          {groups.length === 0 ? (
            <div className="kso-gate"><p>Nothing on your punch list.</p></div>
          ) : null}
        </div>
      </main>
    );
  }

  const meta = [
    item.room || item.location,
    item.visit_label ? `from ${item.visit_label}` : '',
    item.assigned_at ? `assigned ${shortDate(item.assigned_at)}` : '',
    item.state === 'closed' ? `Closed ${shortDate(item.closed_at)}` : punchWord(item.state),
  ].filter(Boolean).join(' · ');

  return (
    <main className="kso-page" data-testid="site-office-punch-item">
      <div className="kso-wrap">
        {strip('Punch item')}
        {head}
        {crumb(item.title)}
        <div className="kso-grid">
        <section className="kso-card w12">
          <div className="kso-card__bd">
            <Hero photo={(item.photos || [])[0]} alt={item.title} />
            <div className="kso-item__t" data-testid="site-office-item-title">{item.title}</div>
            <div className="kso-item__meta" data-testid="site-office-item-meta">{meta}</div>
            {item.description ? (
              <p data-testid="site-office-item-description">{item.description}</p>
            ) : null}
            {item.reopen_note ? (
              <div className="kso-punch__back" data-testid="site-office-item-reopened">
                Sent back: {item.reopen_note}
              </div>
            ) : null}
            {(item.attached || []).length ? (
              <div className="kso-item__meta" data-testid="site-office-item-attached">
                Attached ·{' '}
                {(item.attached || []).map((a, i) => (
                  <span key={a.label}>
                    {i ? ' · ' : ''}
                    {a.url ? <a href={a.url} target="_blank" rel="noreferrer">{a.label}</a> : a.label}
                  </span>
                ))}
              </div>
            ) : null}
            {item.done ? (
              <div className="kso-item__meta" data-testid="site-office-item-done">
                Done by {item.done.by} · {shortDate(item.done.at)}
                {item.done.note ? ` — ${item.done.note}` : ''}
              </div>
            ) : null}

            {item.state === 'closed' ? null : (
              <div className="kso-acts">
                {pending ? (
                  <button type="button" className="kso-btn brand" disabled
                    data-testid="site-office-punch-pending">{pending}</button>
                ) : item.state === 'ready_for_review' ? (
                  <button type="button" className="kso-btn brand" disabled
                    data-testid="site-office-item-waiting">Waiting for {firmShort} to review</button>
                ) : (
                  <>
                    <label className="kso-btn" data-testid="site-office-item-pick"
                      title={lockedTitle} aria-disabled={Boolean(preview)}>
                      {shots.length ? `${shots.length} photo(s) ready` : 'Photo of the work'}
                      <input type="file" accept="image/*" capture="environment" hidden
                        disabled={Boolean(preview)}
                        onChange={(e) => { setShots(Array.from(e.target.files || []).slice(0, 5)); setError(''); }} />
                    </label>
                    <button type="button" className="kso-btn brand" disabled={busy || Boolean(preview)}
                      title={lockedTitle}
                      onClick={markDone} data-testid="site-office-item-mark-done">
                      {busy ? 'Sending…' : 'Mark done · add photo'}
                    </button>
                  </>
                )}
                <button type="button" className="kso-btn" onClick={() => setAsking((v) => !v)}
                  disabled={Boolean(preview)} title={lockedTitle}
                  data-testid="site-office-item-question">Question</button>
              </div>
            )}
            {item.state === 'open' ? (
              <input className="kso-in" placeholder="What you did (optional)…" value={note}
                onChange={(e) => setNote(e.target.value)} data-testid="site-office-item-note" />
            ) : null}
            {asking ? (
              <div className="kso-compose">
                <input className="kso-in" placeholder={`Ask ${firmShort} about this item…`}
                  value={question} onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); say(question, 'question'); } }}
                  data-testid="site-office-item-question-input" />
                <button type="button" className="kso-btn brand" onClick={() => say(question, 'question')}
                  disabled={Boolean(preview)} title={lockedTitle}
                  data-testid="site-office-item-question-send">Send question</button>
              </div>
            ) : null}
            {error ? <div className="kso-admin__err" data-testid="site-office-item-error">{error}</div> : null}
          </div>
        </section>

        <section className="kso-card w12" data-testid="site-office-item-thread">
          <div className="kso-card__hd">On this item<span className="n">
            {comments.length ? `${comments.length} in thread` : 'nothing yet'}</span></div>
          <div className="kso-card__bd">
            {comments.map((c) => (
              <div className={`kso-msg${c.from === 'party' ? ' me' : ''}`} key={c.id}
                data-testid={`site-office-item-comment-${c.id}`}>
                <span className="w">{c.author} · {shortDate(c.at)}{c.kind === 'question' ? ' · question' : ''}</span>
                <span className="b">{c.text}</span>
              </div>
            ))}
            <div className="kso-compose">
              <input className="kso-in" placeholder={`A line for ${firmShort}…`} value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); say(draft, 'comment'); } }}
                data-testid="site-office-item-comment-input" />
              <button type="button" className="kso-btn brand" onClick={() => say(draft, 'comment')}
                disabled={Boolean(preview)} title={lockedTitle}
                data-testid="site-office-item-comment-send">Send</button>
            </div>
          </div>
        </section>
        </div>
        <div className="kso-foot">{firm} · this link is personal to {office.party?.name}</div>
      </div>
    </main>
  );
}
